import { serverIntent } from '../../core/serverIntent.js';
import { GameState }    from '../../core/GameState.js';
import { EventBus }     from '../../core/EventBus.js';
import { CARGO_TYPES, CARGO_CATEGORIES, canShipCarryCargo } from '../../config/cargoTypes.js';
import { isFeatureUnlocked } from '../../config/research.js';
import { formatMoney }  from '../../utils/formatting.js';
import { calculateRoute } from '../../utils/seaRoutes.js';
import { debounce }     from '../../utils/debounce.js';
import { findBestTrade, findBestTradeFleetwide, rankTradesByProfit } from './MarketAnalystHelper.js';

const SIZE_ORDER = { tiny: 0, small: 1, medium: 2, large: 3, major: 4 };
const SIZE_LABELS = ['tiny', 'small', 'medium', 'large', 'major'];

/**
 * Market tab: shows current cargo prices at all ports.
 */
export class MarketPanel {
  constructor(uiManager) {
    this.ui = uiManager;
    this.portFilter = 'all';
    this.sortBy = null;   // null = auto-detect on first render
    this.sortShipId = null;
    // Per-ship size filter: shipId -> Set of visible sizes. Working cache,
    // seeded from (and persisted back to) the ship's own uiFilters so the
    // selection survives switching ships / reconnecting.
    this.shipSizeFilters = new Map();
    // Global size filter (when no ship selected) — nothing to remember
    // this by, so it stays session-only.
    this.globalSizeFilter = new Set(SIZE_LABELS);
    this.cargoCategoryFilter = null;  // null = auto-detect (ship if selected, all otherwise)
    // Sea distance cache: keyed by reference portId
    this._seaDistCache = null;
    this._seaDistCachePortId = null;
    // Debounced so rapid toggles (several checkboxes in a row) don't fire
    // one network round-trip per click.
    this._saveShipFilters = debounce((shipId, filters) => {
      serverIntent('ship/ui-filters', { shipId, filters });
    }, 600);
  }

  _getSizeFilter() {
    if (this.sortShipId) {
      if (!this.shipSizeFilters.has(this.sortShipId)) {
        const ship = GameState.getShip(this.sortShipId);
        const saved = ship?.uiFilters?.marketSizeFilter;
        this.shipSizeFilters.set(this.sortShipId, saved ? new Set(saved) : new Set(SIZE_LABELS));
      }
      return this.shipSizeFilters.get(this.sortShipId);
    }
    return this.globalSizeFilter;
  }

  _saveSizeFilter() {
    if (!this.sortShipId) return;
    this._saveShipFilters(this.sortShipId, { marketSizeFilter: Array.from(this._getSizeFilter()) });
  }

  _saveCargoCategoryFilter() {
    if (!this.sortShipId) return;
    this._saveShipFilters(this.sortShipId, { marketCargoCategoryFilter: this.cargoCategoryFilter });
  }

  _getAnalyst() {
    if (!GameState.marketAnalyst) return null;
    return GameState.crewMembers.get(GameState.marketAnalyst);
  }

  /**
   * UIManager asks before re-rendering the active tab. The price table is
   * ~18k cells and takes the better part of a second to rebuild, so ignore
   * the per-second ship ticks (fuel, condition, handling, maintenance) and
   * anything else that can't change what the table shows.
   */
  shouldRerender(event, data) {
    switch (event) {
      case 'market:updated':
      case 'game:loaded':
      case 'ship:arrived':
      case 'ship:departed':
      case 'ship:purchased':
      case 'ship:sold':
      case 'research:completed':
      case 'permit:completed':
      case 'staff:analystHired':
      case 'staff:analystFired':
        return true;
      case 'cargo:loaded':
      case 'cargo:unloaded':
        // Loaded cargo is highlighted for the reference ship only
        return (data?.ship?.id || data?.shipId) === this.sortShipId;
      default:
        return false;
    }
  }

  render() {
    const container = document.querySelector('.panel-content');

    // If the market is already rendered, just update the table data
    // (preserves scroll) — coalesced to one rebuild per frame, since
    // updates tend to arrive in bursts (e.g. two market:updated in a row).
    const existingWrap = container.querySelector('.market-table-wrap');
    if (existingWrap && container.querySelector('.market-header')) {
      if (this._tableFrame) return;
      this._tableFrame = requestAnimationFrame(() => {
        this._tableFrame = null;
        if (document.querySelector('.market-table-wrap')) this._renderTable();
      });
      return;
    }

    // Build ship options for distance sort
    const ships = GameState.getAllShips();
    const dockedShips = ships.filter(s => s.currentPortId);
    const shipOptions = dockedShips
      .map(s => `<option value="${s.id}">${s.name}</option>`)
      .join('');

    // Auto-default to distance sort if a docked ship is available
    if (this.sortBy === null) {
      this.sortBy = dockedShips.length > 0 ? 'distance' : 'name';
    }

    // Auto-default cargo filter: the selected ship's remembered choice if it
    // has one, else "This Ship" if a ship is selected, else "All"
    if (this.cargoCategoryFilter === null) {
      const saved = this.ui.selectedShip?.uiFilters?.marketCargoCategoryFilter;
      this.cargoCategoryFilter = saved
        || ((this.ui.selectedShip && this.ui.selectedShip.currentPortId) ? 'ship' : 'all');
    }

    const sizeFilter = this._getSizeFilter();
    const sizeButtons = SIZE_LABELS.map(s =>
      `<button class="bfilter market-size-btn ${sizeFilter.has(s) ? 'active' : ''}" data-size="${s}">${s}</button>`
    ).join('');

    // Cargo category filter buttons
    let cargoCatButtons = `<button class="bfilter market-cargo-cat${this.cargoCategoryFilter === 'all' ? ' active' : ''}" data-cargo-cat="all">All</button>`;
    cargoCatButtons += `<button class="bfilter market-cargo-cat${this.cargoCategoryFilter === 'ship' ? ' active' : ''}" data-cargo-cat="ship" style="display:${this.sortShipId ? '' : 'none'}">This Ship</button>`;
    for (const [catId, catDef] of Object.entries(CARGO_CATEGORIES)) {
      cargoCatButtons += `<button class="bfilter market-cargo-cat${this.cargoCategoryFilter === catId ? ' active' : ''}" data-cargo-cat="${catId}">${catDef.name}</button>`;
    }

    // Analyst section
    const analystHtml = this._renderAnalystSection();

    // Use flex layout so the table area fills remaining space and scrolls internally
    container.classList.add('panel-content--market');
    container.innerHTML = `
      <div class="market-header">
        <div class="market-header-top">
          <h4>Global Market Prices</h4>
          <div class="market-controls">
            <label class="market-sort-label">Sort:
              <select id="market-sort">
                <option value="name">Name</option>
                <option value="distance">Distance</option>
                <option value="size">Size</option>
                <option value="region">Region</option>
              </select>
            </label>
            <select id="market-sort-ship" class="market-sort-ship-hidden"
              title="Select ship for distance reference">
              ${shipOptions || '<option value="">No docked ships</option>'}
            </select>
            <select id="market-port-filter"><option value="all">All Ports</option></select>
          </div>
        </div>
        ${analystHtml}
        <div class="market-size-filters">${sizeButtons}</div>
        <div class="trade-filters">${cargoCatButtons}</div>
      </div>
      <div class="market-table-wrap"><table class="market-table"></table></div>`;

    // Hovering a port column header flashes that port on the map (no pan).
    // Delegated on the wrapper, which survives _renderTable() redraws; only
    // fires when the pointer moves onto a different port's header.
    const tableWrap = container.querySelector('.market-table-wrap');
    let hoveredPortId = null;
    tableWrap.addEventListener('mouseover', (e) => {
      const portId = e.target.closest('th[data-port-id]')?.dataset.portId || null;
      if (portId === hoveredPortId) return;
      hoveredPortId = portId;
      if (portId) EventBus.emit('map:flashPort', { portId });
    });
    tableWrap.addEventListener('mouseleave', () => { hoveredPortId = null; });

    // Port filter
    const sel = document.getElementById('market-port-filter');
    for (const port of GameState.getAllPorts()) {
      const opt = document.createElement('option');
      opt.value = port.id;
      opt.textContent = port.name;
      sel.appendChild(opt);
    }
    sel.value = this.portFilter;
    sel.addEventListener('change', () => {
      this.portFilter = sel.value;
      this._renderTable();
    });

    // Sort selector
    const sortSel = document.getElementById('market-sort');
    sortSel.value = this.sortBy;
    sortSel.addEventListener('change', () => {
      this.sortBy = sortSel.value;
      this._toggleShipSelector();
      this._renderTable();
    });

    // Ship selector for distance sort — sync from UIManager's selected ship
    const shipSel = document.getElementById('market-sort-ship');
    if (this.ui.selectedShip) {
      this.sortShipId = this.ui.selectedShip.id;
    }
    if (this.sortShipId) shipSel.value = this.sortShipId;
    else if (ships.length) this.sortShipId = ships.find(s => s.currentPortId)?.id || null;
    if (this.sortShipId) shipSel.value = this.sortShipId;
    shipSel.addEventListener('change', () => {
      this.selectShip(shipSel.value);
    });

    // Size filter buttons
    container.querySelectorAll('.market-size-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const size = btn.dataset.size;
        const filter = this._getSizeFilter();
        if (filter.has(size)) {
          if (filter.size > 1) filter.delete(size);
        } else {
          filter.add(size);
        }
        btn.classList.toggle('active', filter.has(size));
        this._saveSizeFilter();
        this._renderTable();
      });
    });

    // Cargo category filter buttons
    container.querySelectorAll('.market-cargo-cat').forEach(btn => {
      btn.addEventListener('click', () => {
        this.cargoCategoryFilter = btn.dataset.cargoCat;
        this._saveCargoCategoryFilter();
        container.querySelectorAll('.market-cargo-cat').forEach(b => b.classList.toggle('active', b.dataset.cargoCat === this.cargoCategoryFilter));
        this._renderTable();
      });
    });

    // Analyst UI event handlers
    this._attachAnalystHandlers();

    this._toggleShipSelector();
    this._renderTable();
  }

  _renderAnalystSection() {
    const analyst = this._getAnalyst();

    if (!analyst) {
      return `
        <div class="analyst-section">
          <div class="analyst-status">
            <span class="analyst-label">Market Analyst:</span>
            <span class="analyst-value">None — visit a medium+ port to hire</span>
          </div>
        </div>
      `;
    } else {
      const tierLabel = analyst.skillTier.charAt(0).toUpperCase() + analyst.skillTier.slice(1);
      const remaining = analyst.getContractRemainingDays(GameState.currentDate);
      const penalty = analyst.getTerminationPenalty(GameState.currentDate);
      const contractText = remaining > 0
        ? `${remaining}d remaining`
        : 'Contract expired';
      const penaltyText = penalty > 0 ? ` — penalty ${formatMoney(penalty)}` : '';
      return `
        <div class="analyst-section">
          <div class="analyst-status">
            <span class="analyst-label">Market Analyst:</span>
            <span class="analyst-value">${tierLabel} - ${analyst.name} (${formatMoney(analyst.dailySalary)}/day)</span>
            <button class="btn btn-small btn-danger" id="analyst-fire-btn">Fire</button>
          </div>
          <div class="analyst-contract-info">${contractText}${penaltyText}</div>
        </div>
      `;
    }
  }

  _attachAnalystHandlers() {
    const fireBtn = document.getElementById('analyst-fire-btn');

    if (fireBtn) {
      fireBtn.addEventListener('click', async () => {
        const analyst = this._getAnalyst();
        if (!analyst) return;
        const penalty = analyst.getTerminationPenalty(GameState.currentDate);
        const msg = penalty > 0
          ? `Fire ${analyst.name}? Early termination penalty: ${formatMoney(penalty)}`
          : `Fire ${analyst.name}? Contract expired — no penalty.`;
        if (confirm(msg)) {
          await serverIntent('crew/fire-analyst', {});
          this.render();
        }
      });
    }
  }

  /** Called externally when a ship is selected (e.g. clicking map marker) */
  selectShip(shipId) {
    this.sortShipId = shipId;
    this._seaDistCachePortId = null;
    const ship = GameState.getShip(shipId);

    // Auto-filter: restrict to cargo this ship can carry — but only the
    // first time this ship is ever selected. Once the player has picked a
    // filter for this ship (persisted on ship.uiFilters), that choice
    // sticks and this auto-default no longer overrides it.
    if (ship?.uiFilters?.marketCargoCategoryFilter == null) {
      this.cargoCategoryFilter = 'ship';
      this._saveCargoCategoryFilter();
    } else {
      this.cargoCategoryFilter = ship.uiFilters.marketCargoCategoryFilter;
    }

    // Auto-filter: restrict to port sizes the ship can dock at — same
    // "only until customized" rule as above.
    if (ship?.type && ship.uiFilters?.marketSizeFilter == null) {
      const cap = ship.type.cargoCapacity;
      const filter = this._getSizeFilter();
      filter.clear();
      for (const s of SIZE_LABELS) {
        const limit = { tiny: 100, small: 500, medium: 2000, large: 8000, major: 99999 }[s];
        if (cap <= limit) filter.add(s);
      }
      this._saveSizeFilter();
    }

    const shipSel = document.getElementById('market-sort-ship');
    if (shipSel) {
      shipSel.value = shipId;
      this._updateSizeButtons();
      this._updateCargoCatButtons();
      this._renderTable();
    }
  }

  _updateCargoCatButtons() {
    document.querySelectorAll('.market-cargo-cat').forEach(btn => {
      btn.classList.toggle('active', btn.dataset.cargoCat === this.cargoCategoryFilter);
      if (btn.dataset.cargoCat === 'ship') btn.style.display = this.sortShipId ? '' : 'none';
    });
  }

  _toggleShipSelector() {
    const shipSel = document.getElementById('market-sort-ship');
    if (!shipSel) return;
    shipSel.classList.toggle('market-sort-ship-hidden', this.sortBy !== 'distance');
  }

  _updateSizeButtons() {
    const filter = this._getSizeFilter();
    document.querySelectorAll('.market-size-btn').forEach(btn => {
      btn.classList.toggle('active', filter.has(btn.dataset.size));
    });
  }

  _sortPorts(ports) {
    const sorted = [...ports];
    switch (this.sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'size':
        sorted.sort((a, b) => (SIZE_ORDER[b.size] ?? 0) - (SIZE_ORDER[a.size] ?? 0));
        break;
      case 'region':
        sorted.sort((a, b) => (a.region || '').localeCompare(b.region || '') || a.name.localeCompare(b.name));
        break;
      case 'distance': {
        const ship = this.sortShipId ? GameState.getShip(this.sortShipId) : null;
        const refPort = ship?.currentPortId ? GameState.getPort(ship.currentPortId) : null;
        if (refPort) {
          // Use cached sea distances if same reference port
          if (this._seaDistCachePortId !== refPort.id) {
            this._seaDistCache = new Map();
            this._seaDistCachePortId = refPort.id;
            for (const p of GameState.getAllPorts()) {
              if (p.id === refPort.id) {
                this._seaDistCache.set(p.id, 0);
              } else {
                const route = calculateRoute(refPort.position, p.position);
                this._seaDistCache.set(p.id, route.distance);
              }
            }
          }
          this._distCache = this._seaDistCache;
          sorted.sort((a, b) => (this._distCache.get(a.id) || 0) - (this._distCache.get(b.id) || 0));
        } else {
          this._distCache = null;
          sorted.sort((a, b) => a.name.localeCompare(b.name));
        }
        break;
      }
    }
    return sorted;
  }

  _renderTable() {
    // Sync button DOM to match internal state (prevents stale button highlights)
    this._updateSizeButtons();
    this._updateCargoCatButtons();

    // Reference port: where the selected ship is docked (buy prices)
    const ship = this.sortShipId ? GameState.getShip(this.sortShipId) : null;
    const refPort = ship?.currentPortId ? GameState.getPort(ship.currentPortId) : null;

    let ports = this.portFilter === 'all'
      ? GameState.getAllPorts()
      : [GameState.getPort(this.portFilter)].filter(Boolean);

    // Apply size filter when showing all ports
    if (this.portFilter === 'all') {
      const sizeFilter = this._getSizeFilter();
      ports = ports.filter(p => sizeFilter.has(p.size));
      this._distCache = null;
      ports = this._sortPorts(ports);
    }

    // The ship's port and the port open in the side panel are always shown,
    // first, regardless of the size filter or the single-port selector
    const pinned = [...new Set([refPort, this.ui.selectedPort].filter(Boolean))];
    ports = [...pinned, ...ports.filter(p => !pinned.includes(p))];

    const table = document.querySelector('.market-table');
    const types = Object.values(CARGO_TYPES);

    // Determine cargo relevance for sorting rows
    const portStock = refPort ? GameState.portStock.get(refPort.id) || {} : {};
    const loadedTypes = new Set();
    if (ship) {
      for (const c of ship.cargo) loadedTypes.add(c.type);
    }

    // Sort cargo: loaded first, then compatible by stock (highest first), then incompatible
    const shipType = ship?.type || null;
    const sortedTypes = [...types].sort((a, b) => {
      const aCompat = shipType ? canShipCarryCargo(shipType, a.id) : true;
      const bCompat = shipType ? canShipCarryCargo(shipType, b.id) : true;
      if (aCompat !== bCompat) return aCompat ? -1 : 1;
      const aLoaded = loadedTypes.has(a.id) ? 0 : 1;
      const bLoaded = loadedTypes.has(b.id) ? 0 : 1;
      if (aLoaded !== bLoaded) return aLoaded - bLoaded;
      const aStock = portStock[a.id] || 0;
      const bStock = portStock[b.id] || 0;
      return bStock - aStock;
    });

    // Apply cargo category filter
    const effectiveFilter = this.cargoCategoryFilter || 'all';
    const filteredTypes = sortedTypes.filter(cargo => {
      if (effectiveFilter === 'all') return true;
      if (effectiveFilter === 'ship') return shipType ? canShipCarryCargo(shipType, cargo.id) : true;
      return cargo.category === effectiveFilter;
    });

    // Update "This Ship" button visibility
    const shipBtn = document.querySelector('[data-cargo-cat="ship"]');
    if (shipBtn) shipBtn.style.display = this.sortShipId ? '' : 'none';

    // Header with <thead> for sticky
    let header = '<thead><tr><th class="market-col-cargo">Cargo</th>';
    for (const p of ports) {
      let extra = '';
      if (this.sortBy === 'distance' && this._distCache) {
        const d = this._distCache.get(p.id);
        if (d != null) extra = `<span class="market-dist">${Math.round(d)} nm</span>`;
      }
      const isRef = refPort && p.id === refPort.id;
      const label = isRef ? `<span class="market-port-buy">${p.name} (buy)</span>` : p.name;
      header += `<th data-port-id="${p.id}">${label}${extra}</th>`;
    }
    header += '</tr></thead>';

    // Market coloring flags (cached once, not per-cell)
    const hasHints    = refPort ? isFeatureUnlocked('market_hints') : false;
    const hasGradients = hasHints ? isFeatureUnlocked('market_gradients') : false;
    const refPrices   = refPort ? GameState.marketPrices.get(refPort.id) || {} : null;

    // Get analyst tier for feature gating
    const analyst = this._getAnalyst();
    const analystTier = analyst ? analyst.skillTier : null;

    // Rows — show buy price at refPort, sell price at all other ports
    let rows = '<tbody>' + filteredTypes.map(cargo => {
      const isLoaded = loadedTypes.has(cargo.id);
      const isAvailable = (portStock[cargo.id] || 0) > 0;
      const isCompatible = shipType ? canShipCarryCargo(shipType, cargo.id) : true;
      const incompatClass = (!isCompatible && ship) ? ' market-row-incompat' : '';
      const dimClass = (!isLoaded && !isAvailable && refPort && isCompatible) ? ' market-row-dim' : '';
      const loadedClass = isLoaded ? ' market-row-loaded' : '';
      const refPrice = refPrices ? refPrices[cargo.id] : null;
      let row = `<tr class="${incompatClass}${dimClass}${loadedClass}"><td class="market-col-cargo">${cargo.name}</td>`;
      for (const p of ports) {
        const prices = GameState.marketPrices.get(p.id);
        const rawPrice = prices ? prices[cargo.id] : null;
        if (rawPrice == null) { row += '<td>—</td>'; continue; }

        // Port-terminal check (src/entities/Port.js's canHandleCargo) — a
        // price still gets computed/cached for every cargo at every port
        // (EconomySystem.calculatePrice doesn't know about terminals), but
        // trading it is hard-blocked without the matching terminal, so show
        // that instead of a price that would just fail if clicked. This is
        // a PER-CELL check (which port, not just which cargo), unlike the
        // ship-compatibility greying below which is the same for every
        // column since it only depends on the reference ship.
        if (!p.canHandleCargo(cargo.id)) {
          row += '<td class="market-cell-no-terminal" title="No terminal for this cargo category at ' + p.name + '">—</td>';
          continue;
        }

        // Senior+ analyst: trend arrow (src/systems/EconomySystem.js's
        // persistent per-port-cargo drift, nudged every 6 game-hours) — only
        // shown when actually moving, to avoid an arrow on every one of the
        // ~7,000 port-cargo cells in the full table.
        let trendArrow = '';
        if (analystTier === 'senior' || analystTier === 'expert') {
          const direction = GameState.marketTrends.get(p.id)?.[cargo.id];
          if (direction === 'rising') {
            trendArrow = ' <span class="trend-arrow trend-up" title="Price trending up">▲</span>';
          } else if (direction === 'falling') {
            trendArrow = ' <span class="trend-arrow trend-down" title="Price trending down">▼</span>';
          }
        }

        const isRef = refPort && p.id === refPort.id;
        let displayPrice, tag = '';
        if (isRef) {
          // Buy price at current port (full market price)
          displayPrice = rawPrice;

          // Junior+ analyst: Port tags for good buy opportunities
          if (analystTier && cargo.basePrice) {
            const priceDiff = (displayPrice - cargo.basePrice) / cargo.basePrice;
            if (priceDiff < -0.15) {
              tag = ' <span class="port-tag good-buy">good buy</span>';
            }
          }
        } else {
          // Sell price at destination: 0.5x if producing, 0.9x otherwise
          const isProduced = p.producedGoods.includes(cargo.id);
          const sellMod = isProduced ? 0.5 : 0.9;
          displayPrice = Math.round(rawPrice * sellMod);
          if (isProduced) tag = ' <span class="produced-tag">-50%</span>';

          // Junior+ analyst: Port tags for good sell opportunities
          if (analystTier && !isProduced && cargo.basePrice) {
            const priceDiff = (rawPrice - cargo.basePrice) / cargo.basePrice;
            if (priceDiff > 0.15) {
              tag = ' <span class="port-tag good-sell">good sell</span>';
            }
          }
        }

        let style = '';
        let extraInfo = '';

        // Market hints & gradients (existing research-based feature)
        if (hasHints && !isRef && refPrice != null && refPrice > 0) {
          const diff = (displayPrice - refPrice) / refPrice;

          if (Math.abs(diff) > 0.05) {
            let alpha = hasGradients ? Math.min(0.4, Math.abs(diff) * 0.8) : 0.12;

            // Junior+ analyst: Enhanced highlighting for high profit / losses
            if (analystTier) {
              if (diff > 0.5) alpha = Math.min(0.6, alpha * 1.5);
              if (diff < 0) alpha = Math.min(0.5, alpha * 1.2);
            }

            const color = diff > 0 ? `rgba(46,204,113,${alpha})` : `rgba(231,76,60,${alpha})`;
            style = ` style="background:${color}"`;
          }

          // Senior+ analyst: Show profit % and profit/nm
          if (analystTier === 'senior' || analystTier === 'expert') {
            if (ship && refPort && !isRef) {
              const distance = this._distCache ? this._distCache.get(p.id) : null;
              if (distance != null && distance > 0) {
                const profitPercent = ((displayPrice - refPrice) / refPrice * 100).toFixed(0);
                const fuelNeeded = ship.estimateFuelForVoyage(distance);
                const fuelCost = Math.round(fuelNeeded * refPort.fuelPrice);
                const quantity = Math.min(ship.getAvailableCapacity(), portStock[cargo.id] || 0);
                const netProfit = (displayPrice - refPrice) * quantity - fuelCost;
                const profitPerNm = netProfit / distance;

                extraInfo = `<span class="analyst-extra">${profitPercent}% (${formatMoney(profitPerNm)}/nm)</span>`;
              }
            }
          }
        }

        row += `<td${style}>${formatMoney(displayPrice)}${trendArrow}${tag}${extraInfo}</td>`;
      }
      row += '</tr>';
      return row;
    }).join('') + '</tbody>';

    // Preserve scroll position across table rebuild
    const wrap = document.querySelector('.market-table-wrap');
    const scrollLeft = wrap ? wrap.scrollLeft : 0;
    const scrollTop  = wrap ? wrap.scrollTop  : 0;

    table.innerHTML = header + rows;

    if (wrap) {
      wrap.scrollLeft = scrollLeft;
      wrap.scrollTop  = scrollTop;
    }

    // Render expert analyst recommendation panel
    if (analystTier === 'expert') {
      this._renderExpertRecommendations();
    }
  }

  _renderExpertRecommendations() {
    const ship = this.sortShipId ? GameState.getShip(this.sortShipId) : null;
    const refPort = ship?.currentPortId ? GameState.getPort(ship.currentPortId) : null;

    // Remove any existing recommendation panel
    const existingPanel = document.querySelector('.analyst-recommendations');
    if (existingPanel) existingPanel.remove();

    if (!ship || !refPort || ship.status !== 'docked') return;

    const bestTrade = findBestTrade(ship);

    if (!bestTrade) return;

    const panel = document.createElement('div');
    panel.className = 'analyst-recommendations';
    panel.innerHTML = `
      <h5>Optimal Trade Recommendation</h5>
      <div class="analyst-rec-content">
        <div class="analyst-rec-row">
          <strong>Ship:</strong> ${ship.name}
        </div>
        <div class="analyst-rec-row">
          <strong>Cargo:</strong> ${bestTrade.cargoName} (${bestTrade.quantity} TEU)
        </div>
        <div class="analyst-rec-row">
          <strong>Buy at:</strong> ${refPort.name} for ${formatMoney(bestTrade.buyPrice)}/TEU
        </div>
        <div class="analyst-rec-row">
          <strong>Sell at:</strong> ${bestTrade.destPort.name} for ${formatMoney(bestTrade.sellPrice)}/TEU
        </div>
        <div class="analyst-rec-row">
          <strong>Distance:</strong> ${Math.round(bestTrade.distance)} nm
        </div>
        <div class="analyst-rec-row">
          <strong>Fuel Cost:</strong> ${formatMoney(bestTrade.fuelCost)}
        </div>
        <div class="analyst-rec-row analyst-rec-profit">
          <strong>Net Profit:</strong> <span class="profit-value">${formatMoney(bestTrade.profit)}</span> (${(bestTrade.profitPercent * 100).toFixed(0)}% margin)
        </div>
      </div>
      <button class="btn btn-small analyst-fleet-scan-btn" id="analyst-fleet-scan">Show Best Fleet Trade</button>
      <div id="analyst-fleet-result" style="display:none;"></div>
    `;

    const header = document.querySelector('.market-header');
    if (header) {
      header.appendChild(panel);

      // Attach fleet scan handler
      const fleetScanBtn = document.getElementById('analyst-fleet-scan');
      const fleetResultDiv = document.getElementById('analyst-fleet-result');
      if (fleetScanBtn && fleetResultDiv) {
        fleetScanBtn.addEventListener('click', () => {
          const fleetTrade = findBestTradeFleetwide();
          if (fleetTrade) {
            const currentPort = GameState.getPort(fleetTrade.ship.currentPortId);
            fleetResultDiv.style.display = 'block';
            fleetResultDiv.innerHTML = `
              <div class="analyst-fleet-trade">
                <strong>Best Fleet Trade:</strong> ${fleetTrade.ship.name} at ${currentPort.name}<br>
                ${fleetTrade.cargoName} (${fleetTrade.quantity} TEU) to ${fleetTrade.destPort.name}<br>
                Profit: <span class="profit-value">${formatMoney(fleetTrade.profit)}</span> (${(fleetTrade.profitPercent * 100).toFixed(0)}%)
              </div>
            `;
          } else {
            fleetResultDiv.style.display = 'block';
            fleetResultDiv.innerHTML = '<div class="analyst-fleet-trade">No profitable trades found across fleet.</div>';
          }
        });
      }
    }
  }
}
